
import React from "react";
import Layout from "@/components/Layout";
import PageMeta from "@/components/seo/PageMeta";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { BookOpen, Quote, Star, ExternalLink } from "lucide-react";

const BookPage: React.FC = () => {
  return (
    <Layout>
      <PageMeta
        title="The AI Governance Book | BoardSight AI"
        description="Essential reading for board directors on AI oversight, governance frameworks, and strategic AI risk management."
      />
      
      <div className="bg-gradient-to-b from-techne-primaryPurple/10 to-transparent">
        <div className="container-custom pt-16 pb-20">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-techne-primaryPurple mb-6">
                  The Board's Guide to AI Oversight
                </h1>
                <p className="text-xl text-muted-foreground mb-6">
                  A comprehensive guide for directors navigating AI governance, risk management, and strategic oversight in the age of artificial intelligence.
                </p>
                <div className="flex items-center gap-2 mb-6">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                  <span className="text-muted-foreground ml-2">Essential reading for board directors</span>
                </div>
                <div className="flex flex-wrap gap-4">
                  <Button asChild size="lg">
                    <a href="#" target="_blank" rel="noopener noreferrer">
                      Get the Book <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                  <Button asChild variant="outline" size="lg">
                    <Link to="/contact">Request a Review Copy</Link>
                  </Button>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-techne-purple to-techne-primaryPurple rounded-2xl p-8 text-white aspect-[3/4] flex flex-col items-center justify-center shadow-2xl">
                <BookOpen className="h-24 w-24 mb-6 opacity-80" />
                <h2 className="text-2xl font-bold text-center mb-2">The Board's Guide to</h2>
                <h3 className="text-3xl font-bold text-center">AI Oversight</h3>
                <p className="text-sm opacity-75 mt-4">BoardSight AI</p>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-2xl font-bold mb-8 text-center">What You'll Learn</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    title: "Governance Frameworks",
                    description: "Establish effective board-level AI oversight structures and processes."
                  },
                  {
                    title: "Risk Management",
                    description: "Identify, assess, and mitigate AI-related risks across the enterprise."
                  },
                  {
                    title: "Regulatory Landscape",
                    description: "Navigate the EU AI Act and emerging global AI regulations."
                  },
                  {
                    title: "Strategic Questions",
                    description: "The right questions directors should ask management about AI."
                  },
                  {
                    title: "Committee Structures",
                    description: "Design effective AI oversight committees and reporting lines."
                  },
                  {
                    title: "Case Studies",
                    description: "Real-world examples of AI governance successes and failures."
                  }
                ].map((item, index) => (
                  <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
                    <h3 className="font-semibold mb-2">{item.title}</h3>
                    <p className="text-muted-foreground text-sm">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-card rounded-2xl p-8 border shadow-lg">
              <Quote className="h-10 w-10 text-techne-purple mb-4" />
              <blockquote className="text-xl italic mb-4">
                "An indispensable resource for any board member seeking to understand their oversight responsibilities in the AI era. Clear, practical, and actionable."
              </blockquote>
              <p className="text-muted-foreground">
                — Board Director, Fortune 500 Company
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BookPage;
